import React from 'react';

/**
 * Componente per visualizzare errori React non gestiti
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Aggiorna lo stato per mostrare l'interfaccia di errore
        return { 
            hasError: true,
            errorId: `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
    }

    componentDidCatch(error, errorInfo) {
        // Log dell'errore per debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Se è presente un handler per errori personalizzato, chiamalo
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Log per servizi di monitoraggio esterni (Sentry, LogRocket, etc.)
        if (window.gtag) {
            window.gtag('event', 'exception', {
                description: error.toString(),
                fatal: true
            });
        }
    }

    handleRetry = () => {
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Interfaccia di errore personalizzata
            return (
                <ErrorFallback
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                    errorId={this.state.errorId}
                    onRetry={this.handleRetry}
                    onReload={this.handleReload}
                    showDetails={this.props.showDetails}
                    customMessage={this.props.customMessage}
                />
            );
        }

        return this.props.children;
    }
}

/**
 * Componente funzionale per l'interfaccia di errore
 */
const ErrorFallback = ({ 
    error, 
    errorInfo, 
    errorId,
    onRetry, 
    onReload,
    showDetails = false,
    customMessage = null
}) => {
    // Fallback per i colori del tema quando Material-UI non è disponibile
    const fallbackColors = {
        card: { background: '#ffffff', shadow: '0 2px 4px rgba(0,0,0,0.1)' },
        semantic: {
            error: { main: '#f44336' }
        },
        table: {
            header: { background: '#f5f5f5' },
            row: { even: '#fafafa' }
        }
    };

    const isDevelopment = process.env.NODE_ENV === 'development';

    // Messaggi di fallback per quando i18n non è disponibile
    const fallbackMessages = {
        title: 'Oops! Qualcosa è andato storto',
        description: 'Si è verificato un errore imprevisto nell\'applicazione.',
        errorMessage: 'Messaggio di errore:',
        technicalDetails: 'Dettagli tecnici',
        retry: 'Riprova',
        reload: 'Ricarica Pagina',
        home: 'Torna alla Home',
        support: 'Se il problema persiste, contatta il supporto tecnico.',
        supportId: 'Fornisci questo ID di errore:',
        unknown: 'Si è verificato un errore sconosciuto'
    };

    // Usa traduzioni se disponibili, altrimenti fallback
    let t = (key, fallback) => fallback || fallbackMessages[key.split('.').pop()] || key;
    
    // Proviamo a usare le traduzioni senza hooks
    if (typeof window !== 'undefined' && window.i18next) {
        t = window.i18next.t;
    }

    const errorMessage = customMessage || error?.message || t('errors.unknown', fallbackMessages.unknown);
    const errorStack = error?.stack || 'Stack trace non disponibile';
    const componentStack = errorInfo?.componentStack || 'Component stack non disponibile';

    const handleNavigateHome = () => {
        window.location.href = '/';
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            backgroundColor: fallbackColors.card.background
        }}>
            <div style={{
                maxWidth: '800px',
                width: '100%',
                boxShadow: fallbackColors.card.shadow,
                borderRadius: '8px',
                backgroundColor: 'white'
            }}>
                <div style={{ padding: '32px' }}>
                    {/* Header dell'errore */}
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{ 
                            fontSize: '80px', 
                            color: fallbackColors.semantic.error.main,
                            marginBottom: '16px'
                        }}>
                            ⚠️
                        </div>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: '600',
                            color: fallbackColors.semantic.error.main,
                            margin: '0 0 16px 0'
                        }}>
                            {t('error.boundary.title', fallbackMessages.title)}
                        </h1>
                        <p style={{
                            fontSize: '16px',
                            color: '#666',
                            margin: '0',
                            lineHeight: '1.5'
                        }}>
                            {t('error.boundary.description', fallbackMessages.description)}
                        </p>
                    </div>

                    {/* Messaggio di errore */}
                    <div style={{
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '4px',
                        padding: '16px',
                        marginBottom: '24px'
                    }}>
                        <div style={{ fontSize: '14px' }}>
                            <strong>{t('error.boundary.errorMessage', fallbackMessages.errorMessage)}</strong>
                            <br />
                            <span style={{ 
                                color: fallbackColors.semantic.error.main,
                                wordBreak: 'break-word'
                            }}>
                                {errorMessage}
                            </span>
                        </div>
                        {errorId && (
                            <div style={{ marginTop: '8px' }}>
                                <span style={{
                                    backgroundColor: 'white',
                                    border: `1px solid ${fallbackColors.semantic.error.main}`,
                                    color: fallbackColors.semantic.error.main,
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    ID: {errorId}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Dettagli tecnici (solo in sviluppo o se specificato) */}
                    {(isDevelopment || showDetails) && (
                        <details style={{ marginBottom: '24px' }}>
                            <summary style={{
                                backgroundColor: fallbackColors.table.header.background,
                                padding: '12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}>
                                {t('error.boundary.technicalDetails', fallbackMessages.technicalDetails)}
                            </summary>
                            <div style={{ padding: '16px 0' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <h4 style={{ 
                                        margin: '0 0 8px 0',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}>
                                        Error Stack:
                                    </h4>
                                    <pre style={{
                                        backgroundColor: fallbackColors.table.row.even,
                                        padding: '16px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        overflow: 'auto',
                                        maxHeight: '200px',
                                        whiteSpace: 'pre-wrap',
                                        wordWrap: 'break-word',
                                        margin: '0'
                                    }}>
                                        {errorStack}
                                    </pre>
                                </div>
                                
                                <div>
                                    <h4 style={{ 
                                        margin: '0 0 8px 0',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}>
                                        Component Stack:
                                    </h4>
                                    <pre style={{
                                        backgroundColor: fallbackColors.table.row.even,
                                        padding: '16px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        overflow: 'auto',
                                        maxHeight: '200px',
                                        whiteSpace: 'pre-wrap',
                                        wordWrap: 'break-word',
                                        margin: '0'
                                    }}>
                                        {componentStack}
                                    </pre>
                                </div>
                            </div>
                        </details>
                    )}

                    {/* Azioni disponibili */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '16px', 
                        justifyContent: 'center', 
                        flexWrap: 'wrap',
                        marginBottom: '24px'
                    }}>
                        <button
                            onClick={onRetry}
                            style={{
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                minWidth: '140px'
                            }}
                        >
                            🔄 {t('error.boundary.retry', fallbackMessages.retry)}
                        </button>
                        
                        <button
                            onClick={onReload}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                minWidth: '140px'
                            }}
                        >
                            🔄 {t('error.boundary.reload', fallbackMessages.reload)}
                        </button>
                        
                        <button
                            onClick={handleNavigateHome}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#666',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                minWidth: '140px'
                            }}
                        >
                            🏠 {t('error.boundary.home', fallbackMessages.home)}
                        </button>
                    </div>

                    {/* Informazioni di supporto */}
                    <div style={{ textAlign: 'center' }}>
                        <p style={{
                            fontSize: '12px',
                            color: '#666',
                            margin: '0',
                            lineHeight: '1.5'
                        }}>
                            {t('error.boundary.support', fallbackMessages.support)}
                            {errorId && (
                                <>
                                    <br />
                                    {t('error.boundary.supportId', fallbackMessages.supportId)} <code>{errorId}</code>
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Hook per gestire errori in modo programmatico
 */
export const useErrorHandler = () => {
    const [error, setError] = React.useState(null);

    const resetError = React.useCallback(() => {
        setError(null);
    }, []);

    const handleError = React.useCallback((error) => {
        console.error('Error handled by useErrorHandler:', error);
        setError(error);
    }, []);

    // Simula un error boundary per errori async
    if (error) {
        throw error;
    }

    return { handleError, resetError };
};

/**
 * HOC per wrappare componenti con Error Boundary
 */
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
    return (props) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );
};

export default ErrorBoundary;